import { Json } from "deepslate";
import { Datapack } from "mc-datapack-loader";
import { defineStore } from "pinia";
import { ref } from "vue";
import { get, set } from 'idb-keyval'
import { TextComponent } from "../util/TextComponent.js";


export type StoredDatapack = { img: string, text: string, fileHandle?: FileSystemHandle, storedInOpfs?: boolean, modrinthSlug?: string }
export type StoredLastDatapacks = { type: 'opfs', names?: string[], name?: string }

const LAST_DATAPACKS_KEY = 'last-datapack'

export const useRecentStore = defineStore('recents', () => {

    const avalible = "showDirectoryPicker" in window || 'storage' in navigator
    const enabled = ref(false)
    const recents = ref<StoredDatapack[]>([])

    get('recent-datapacks-meta').then((metas: StoredDatapack[]) => {
        if (metas) {
            get('recent-datapacks-handle').then(h => {
                for (var i = 0 ; i < metas.length ; i++){
                    metas[i].fileHandle = h[i]
                }
                recents.value = metas
                enabled.value = true
            })
        }
    })


    function enable() {
        enabled.value = true
        set('recent-datapacks-meta', [])
        set('recent-datapacks-handle', [])
    }

    async function storeAndAddRecent(file: File, datapack: Datapack) {
        const opfsRoot = await navigator.storage.getDirectory()
        const fileHandle = await opfsRoot.getFileHandle(file.name, {create: true})
        const writable = await fileHandle.createWritable()
        await writable.write(file)
        await writable.close()
        addRecentFileHandle(fileHandle, datapack, true)
    }

    // Every stored pack is returned, so multiple packs stay loaded across reloads.
    async function getLastDatapackFiles(): Promise<File[]> {
        if (!('storage' in navigator)) {
            return []
        }

        try {
            const opfsRoot = await navigator.storage.getDirectory()
            const stored = await get<StoredLastDatapacks>(LAST_DATAPACKS_KEY)
            const names = storedLastDatapackNames(stored)

            if (names === undefined) {
                // Migrate zips stored before the list existed by keeping the newest one.
                const legacy = await findNewestStoredZip(opfsRoot)
                if (legacy === undefined) {
                    return []
                }

                await setLastDatapackNames([legacy.name])
                return [legacy]
            }

            const files: File[] = []
            const kept: string[] = []
            for (const name of names) {
                try {
                    const handle = await opfsRoot.getFileHandle(name)
                    files.push(await handle.getFile())
                    kept.push(name)
                } catch {
                    // Pack is no longer in storage, forget it.
                }
            }

            if (kept.length !== names.length) {
                await setLastDatapackNames(kept)
            }

            return files
        } catch {
            return []
        }
    }

    function storedLastDatapackNames(stored: StoredLastDatapacks | undefined): string[] | undefined {
        if (stored?.type !== 'opfs') return undefined
        if (Array.isArray(stored.names)) {
            return stored.names.filter((name): name is string => typeof name === 'string')
        }
        return typeof stored.name === 'string' ? [stored.name] : undefined
    }

    async function findNewestStoredZip(opfsRoot: FileSystemDirectoryHandle): Promise<File | undefined> {
        let newest: File | undefined
        for await (const [, handle] of opfsRoot.entries()) {
            if (handle.kind !== 'file') continue

            const file = await handle.getFile()
            if (!/\.(zip|jar)$/i.test(file.name)) continue
            if (newest === undefined || file.lastModified > newest.lastModified) {
                newest = file
            }
        }
        return newest
    }

    async function setLastDatapackNames(names: string[]) {
        await set(LAST_DATAPACKS_KEY, {type: 'opfs', names} satisfies StoredLastDatapacks)
    }

    async function addRecentFileHandle(fileHandle: FileSystemHandle, datapack: Datapack, storedInOpfs: boolean = false) {
        if (!enabled.value){
            return
        } 

        const old_id = recents.value.findIndex(r => r.fileHandle?.name === fileHandle.name)
        if (old_id >= 0) {
            recents.value.splice(old_id, 1)
        }

        const mcmeta = await datapack.getMcmeta()
        if (mcmeta === undefined){
            console.log('Not storing datapack in recents, as no pack.mcmeta exists')
            return
        }

        addRecent({
            img: await datapack.getImage(),
            text: TextComponent.parse(mcmeta.description).toString().split('\n')[0],
            fileHandle: fileHandle,
            storedInOpfs
        })
    }

    async function addRecentModrinth(datapack: Datapack, slug: string, title: string) {
        if (!enabled.value){
            return
        } 

        const old_id = recents.value.findIndex(r => r.modrinthSlug === slug)
        if (old_id >= 0) {
            recents.value.splice(old_id, 1)
        }

        addRecent({
            img: await datapack.getImage(),
            text: title,
            modrinthSlug: slug
        })
    }

    function addRecent(stored: StoredDatapack) {
        if (recents.value.length >= 10) {
            recents.value.pop()
        }

        recents.value.unshift(stored)

        set('recent-datapacks-meta', recents.value.map(v => {
            return {img: v.img, text: v.text, storedInOpfs: v.storedInOpfs, modrinthSlug: v.modrinthSlug}
        }))

        set('recent-datapacks-handle', recents.value.map(v => {
            return v.fileHandle
        }))

    }


    function removeRecentFileHandle(name: string){
        const recent = recents.value.findIndex(r => r.fileHandle?.name === name)
        if (recent >= 0){
            recents.value.splice(recent, 1)
        }
    }

    return {
        avalible,
        enabled,
        recents,
        enable,
        addRecentFileHandle,
        storeAndAddRecent,
        getLastDatapackFiles,
        setLastDatapackNames,
        removeRecentFileHandle,
        addRecentModrinth
    }
})
